// Settings → «Ημερολόγιο κινητού» (owner 22 Oct 2026): the advisor's appointments and day plan in the iPhone / Android
// calendar through private subscription links served by the AVRASTRO platform (packages/platform/src/calendar-feeds.ts),
// tied to the account (e-mail) he signed in with, per workspace. One link per category — each becomes its own calendar
// on the phone, with its own colour (phones colour a whole subscribed calendar, not single events) — plus «Όλα».
// Honest about it: read-only, refreshed by the phone on its own schedule (Google: hours), only what is synced, the link
// is a secret (anyone with it can read the calendar: new link / turn off at any time), initials by default.
import { useEffect, useState } from 'react';
import type { CalendarFeedStatus } from '@vero/platform';
import { useRuntime } from '../core/runtime.tsx';
import { Icon } from '../core/icons.tsx';
import { useConfirm } from '../core/ui.tsx';
import { navigate } from '../core/router.ts';
import { accountService } from '../core/account-service.ts';
import { qrSvg } from '../core/qr.ts';
import { useAccount } from './account.tsx';

type Sel = { target: HTMLSelectElement };
export function PhoneCalendarCard() {
  const rt = useRuntime();
  const { t, i18n } = rt;
  const a = useAccount();
  const confirm = useConfirm();
  const [st, setSt] = useState<CalendarFeedStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState<'initials' | 'full'>('initials');
  const [qrKey, setQrKey] = useState<string>('all');
  const [help, setHelp] = useState<'iphone' | 'android' | null>('iphone');
  const api = () => accountService()?.api;
  const load = async () => { try { const s = await api()!.calendarFeeds(); setSt(s); setDetail(s.detail); } catch { setSt(null); } };
  useEffect(() => { if (a?.signedIn) void load(); }, [a?.signedIn]);
  const run = async (f: () => Promise<CalendarFeedStatus>, ok?: string) => {
    setBusy(true);
    try { const s = await f(); setSt(s); setDetail(s.detail); if (ok) rt.toast(t(ok), 'success'); }
    catch (e) { rt.toast(t('phonecal.err', { code: String((e as Error).message) }), 'error'); }
    finally { setBusy(false); }
  };
  const base = accountService()?.serverUrl ?? '';
  const urlOf = (f: CalendarFeedStatus['feeds'][number]) => f.https ?? `${base.replace(/\/+$/, '')}${f.path}`;
  const webcalOf = (f: CalendarFeedStatus['feeds'][number]) => f.webcal ?? urlOf(f).replace(/^https?:\/\//, 'webcal://');
  const copy = (s: string) => { void navigator.clipboard?.writeText(s).then(() => rt.toast(t('common.copied'), 'info'), () => rt.toast(s, 'info')); };
  const qrFeed = st?.feeds.find((f) => f.key === qrKey) ?? st?.feeds[0];
  const lang = rt.locale.startsWith('ru') ? 'ru' : rt.locale.startsWith('en') ? 'en' : 'el';

  return (
    <section className="card card-pad" style={{ marginTop: 18 }} data-testid="phone-calendar">
      <div className="section-title"><Icon name="appointment" /> {t('phonecal.title')}</div>
      <p className="small" style={{ marginTop: 0 }}>{t('phonecal.intro')}</p>
      {!a?.signedIn ? <>
        <p className="small muted" data-testid="phonecal-need-account">{t('phonecal.need_account')}</p>
        <button className="btn btn-sm" onClick={() => navigate('/account')}><Icon name="lock" />{t('nav.account')}</button>
      </> : !st ? <p className="small muted">{t('common.loading')}</p> : <>
        <div className="acct-rows">
          <div><span>{t('phonecal.account')}</span><b data-testid="phonecal-email">{st.email || a.email}</b></div>
          <div><span>{t('phonecal.workspace')}</span><span>{st.workspaceName || a.workspaceName}</span></div>
          {st.active && <div><span>{t('phonecal.last_fetch')}</span><span data-testid="phonecal-last">{st.lastFetchAt ? i18n.dateTime(st.lastFetchAt) : t('phonecal.never')}</span></div>}
        </div>
        <fieldset className="phonecal-detail" style={{ border: 0, padding: 0, margin: '10px 0' }}>
          <legend className="small"><b>{t('phonecal.detail')}</b></legend>
          {(['initials', 'full'] as const).map((d) => (
            <label key={d} className="check small"><input type="radio" name="phonecal-detail" checked={detail === d} onChange={() => { setDetail(d); if (st.active) void run(() => api()!.calendarFeedsUpdate({ detail: d }), 'phonecal.saved'); }} data-testid={`phonecal-detail-${d}`} />{t(`phonecal.detail_${d}`)}</label>
          ))}
          <p className="tiny muted" style={{ margin: '2px 0 0' }}>{t('phonecal.detail_note')}</p>
        </fieldset>
        {!st.active ? (
          <button className="btn btn-primary" disabled={busy} onClick={() => void run(() => api()!.calendarFeedsCreate({ detail, lang }), 'phonecal.created')} data-testid="phonecal-create"><Icon name="plus" />{t('phonecal.create')}</button>
        ) : <>
          <div className="phonecal-feeds" data-testid="phonecal-feeds">
            {st.feeds.map((f) => (
              <div key={f.key} className="phonecal-feed" data-testid="phonecal-feed" data-key={f.key}>
                <span className="phonecal-dot" style={{ background: f.color }} aria-hidden="true" />
                <div className="grow" style={{ minWidth: 0 }}>
                  <b className="small">{f.name}</b>
                  <div className="tiny muted">{t(`phonecal.what.${f.key}`)}</div>
                  <code className="tiny phonecal-url" data-testid="phonecal-url">{urlOf(f)}</code>
                </div>
                <div className="row wrap" style={{ gap: 4 }}>
                  <a className="btn btn-sm" href={webcalOf(f)} data-testid="phonecal-webcal">{t('phonecal.subscribe')}</a>
                  <button className="btn btn-sm btn-ghost" onClick={() => copy(urlOf(f))} data-testid="phonecal-copy"><Icon name="copy" />{t('common.copy')}</button>
                  <button className="btn btn-sm btn-ghost" onClick={() => setQrKey(f.key)} aria-pressed={qrKey === f.key} data-testid="phonecal-qr-btn">QR</button>
                </div>
              </div>
            ))}
          </div>
          <p className="tiny muted">{t('phonecal.all_or_each')}</p>
          {qrFeed && <div className="phonecal-qr" data-testid="phonecal-qr">
            <div className="phonecal-qr-img" role="img" aria-label={t('phonecal.qr_alt', { name: qrFeed.name })} dangerouslySetInnerHTML={{ __html: qrSvg(webcalOf(qrFeed)) }} />
            <div className="small"><b>{qrFeed.name}</b><p className="tiny muted" style={{ margin: '4px 0 0' }}>{t('phonecal.qr_hint')}</p>
              <select className="select" style={{ width: 'auto', marginTop: 6 }} value={qrFeed.key} onChange={(e: Sel) => setQrKey(e.target.value)} aria-label={t('phonecal.qr_for')} data-testid="phonecal-qr-select">{st.feeds.map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}</select>
            </div>
          </div>}
          <div className="row wrap" style={{ gap: 8, marginTop: 10 }}>
            <button className="btn btn-sm" disabled={busy} onClick={() => confirm(t('phonecal.regenerate_confirm'), () => void run(() => api()!.calendarFeedsCreate({ regenerate: true, detail, lang }), 'phonecal.regenerated'), true)} data-testid="phonecal-regenerate">{t('phonecal.regenerate')}</button>
            <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => confirm(t('phonecal.revoke_confirm'), () => void run(() => api()!.calendarFeedsRevoke(), 'phonecal.revoked'), true)} data-testid="phonecal-revoke">{t('phonecal.revoke')}</button>
          </div>
        </>}
      </>}
      <div className="phonecal-help" style={{ marginTop: 12 }}>
        <div className="row wrap" style={{ gap: 4 }}>
          <button className={`btn btn-sm${help === 'iphone' ? ' btn-primary' : ''}`} onClick={() => setHelp(help === 'iphone' ? null : 'iphone')} data-testid="phonecal-help-iphone">iPhone</button>
          <button className={`btn btn-sm${help === 'android' ? ' btn-primary' : ''}`} onClick={() => setHelp(help === 'android' ? null : 'android')} data-testid="phonecal-help-android">Android / Google</button>
        </div>
        {help && <ol className="small" data-testid={`phonecal-steps-${help}`} style={{ margin: '8px 0 0', paddingInlineStart: 20 }}>{[1, 2, 3, 4, 5].map((n) => <li key={n}>{t(`phonecal.${help}.${n}`)}</li>)}</ol>}
        <ul className="tiny muted" style={{ margin: '10px 0 0', paddingInlineStart: 18 }} data-testid="phonecal-notes">
          <li>{t('phonecal.note_colour')}</li>
          <li>{t('phonecal.note_refresh')}</li>
          <li>{t('phonecal.note_readonly')}</li>
          <li>{t('phonecal.note_sync')}</li>
          <li>{t('phonecal.note_privacy')}</li>
          <li>{t('phonecal.note_future')}</li>
        </ul>
      </div>
    </section>
  );
}
